import { useCallback, useEffect, useState } from "react";

export type HistoryEntry = {
  id: string;
  episode: number;
  watchedAt: number;
};

const STORAGE_KEY = "juyan:watch-history";
const CHANGE_EVENT = "juyan:history-change";

function readHistory(): HistoryEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item): item is HistoryEntry => {
      if (!item || typeof item !== "object") return false;
      const entry = item as HistoryEntry;
      return typeof entry.id === "string" && typeof entry.episode === "number";
    });
  } catch {
    return [];
  }
}

export function useWatchHistory() {
  const [entries, setEntries] = useState<HistoryEntry[]>([]);

  useEffect(() => {
    setEntries(readHistory());
    const sync = () => setEntries(readHistory());
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const addEntry = useCallback((id: string, episode: number) => {
    const next = [{ id, episode, watchedAt: Date.now() }, ...readHistory().filter((item) => item.id !== id)].slice(0, 24);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      window.dispatchEvent(new Event(CHANGE_EVENT));
    } catch {
      setEntries(next);
    }
  }, []);

  const clearHistory = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      window.dispatchEvent(new Event(CHANGE_EVENT));
    } catch {
      setEntries([]);
    }
  }, []);

  return { entries, addEntry, clearHistory };
}